import React, { useEffect, useRef, useState, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import usePermissions from "../../hooks/usePermissions";
import { reservationsOrdered } from "../../features/reservations/reservationSlice";
import { urgentCheckinsCount } from "../../features/checkin/checkinSlice";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
  isCollapsed: boolean;
  setIsCollapsed: (arg: boolean) => void;
  toggleCollapse: () => void;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen, isCollapsed, setIsCollapsed, toggleCollapse }: SidebarProps) => {
  const location = useLocation();
  const { canRead, isAdmin } = usePermissions();
  const { pathname } = location;

  // Toggle state for Ventas & Reservas accordion
  const [salesOpen, setSalesOpen] = useState(
    pathname.includes('cotizations') || pathname.includes('cruises') || pathname.includes('reservations') || pathname.includes('flights') || pathname.includes('hotel-reservations') || pathname.includes('excursions') || pathname.includes('promotions')
  );
  // Toggle state for Seguimiento accordion (reserved for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [seguimientoOpen, setSeguimientoOpen] = useState(
    pathname.includes('seguimiento')
  );
  // Toggle state for Catálogos accordion
  const [adminOpen, setAdminOpen] = useState(
    pathname.includes('destinations') || pathname.includes('aerolines') || pathname.includes('hotels') || pathname.includes('tours') || pathname.includes('operators') || pathname.includes('cruise-cabins')
  );

  const trigger = useRef<any>(null);
  const sidebar = useRef<any>(null);

  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  // Watch collapsing
  useEffect(() => {
    if (isCollapsed) {
      document.querySelector("body")?.classList.add("sidebar-collapsed");
      setSalesOpen(false);
      setSeguimientoOpen(false);
      setAdminOpen(false);
    } else {
      document.querySelector("body")?.classList.remove("sidebar-collapsed");
    }
  }, [isCollapsed]);

  // Original Corporate Blue hover style
  const styleSelectedLink = `bg-white/15 rounded-xl text-white font-bold border-l-[3px] border-amber-400 shadow-sm`;
  const styleBaseLink = `group relative flex items-center rounded-xl py-2.5 px-4 font-medium text-white/80 dark:text-bodydark1 duration-300 ease-in-out hover:bg-[#a1b1ff] hover:text-[#242022] dark:hover:bg-meta-4 cursor-pointer`;

  // Icon and spacing handling
  const textClass = `overflow-hidden whitespace-nowrap transition-all duration-300 ease-in-out ${isCollapsed ? 'max-w-0 opacity-0 ml-0' : 'max-w-xs opacity-100 ml-3'}`;

  // Sub-link style helper
  const subLinkClass = (isActive: boolean) =>
    `group relative flex items-center gap-2.5 rounded-lg py-2 pl-6 pr-3 font-medium text-xs text-white/80 duration-300 ease-in-out hover:text-white hover:bg-[#a1b1ff] hover:text-[#242022] ${isActive ? 'bg-[#a1b1ff] text-[#242022] font-bold shadow-sm' : ''}`;

  // Computed visibility booleans (derived from permissions)
  const showSalesGroup = canRead('packages') || canRead('cruises') || canRead('flights') || canRead('hotel_reservations') || canRead('excursions') || canRead('promotions');
  const showFinanceGroup = canRead('treasury') || canRead('accounting') || canRead('gastos_operativos');
  const showCatalogGroup = canRead('destinations') || canRead('airlines') || canRead('hotels') || canRead('tours') || canRead('operators') || canRead('cruise_cabins');

  // Badge data from Redux
  const reservations = useSelector(reservationsOrdered);
  const activeReservations = useMemo(() => {
    return reservations.filter((r: any) => {
      const status = r.cotizationDetail?.status;
      return status && status !== 'Cancelado' && status !== 'TERMINADO';
    }).length;
  }, [reservations]);

  // Urgent checkins badge
  const urgentCheckins = useSelector(urgentCheckinsCount);

  // Badge component
  const Badge = ({ count, variant = 'default' }: { count: number, variant?: 'default' | 'danger' }) => {
    if (count === 0 || isCollapsed) return null;
    return (
      <span className={`ml-auto px-1.5 py-0.5 text-[10px] font-bold rounded-full min-w-[20px] text-center leading-none ${
        variant === 'danger'
          ? 'bg-red-500 text-white animate-pulse'
          : 'bg-white/20 text-white/90'
      }`}>
        {count > 99 ? '99+' : count}
      </span>
    );
  };

  return (
    <aside
      ref={sidebar}
      className={`absolute left-0 top-0 z-999 flex h-full flex-col overflow-y-hidden bg-[#3245a4] shadow-lg xl:shadow-none duration-300 ease-in-out dark:bg-boxdark lg:static lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} ${isCollapsed ? 'w-20' : 'w-[260px]'}`}
    >
      {/* <!-- SIDEBAR HEADER (mobile close only) --> */}
      <div className={`flex items-center justify-end py-3 px-4 lg:hidden`}>
        <button
          ref={trigger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-controls="sidebar"
          aria-expanded={sidebarOpen}
          className="text-white"
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="no-scrollbar flex flex-col flex-1 overflow-y-auto overflow-x-hidden duration-300 ease-linear">
        {/* <!-- Sidebar Menu --> */}
        <nav className="mt-2 py-4 px-3">

          {/* 1. OPERACIONES Y CRM */}
          {(canRead('dashboard') || canRead('customers') || canRead('chats')) && (
            <div>
              <h3 className={`mb-3 text-[10px] font-bold tracking-widest text-[#64748B] uppercase duration-300 ${isCollapsed ? 'opacity-0 h-0 hidden' : 'opacity-100 px-4'}`}>
                Operaciones
              </h3>

              <ul className="mb-6 flex flex-col gap-2">
                {canRead('dashboard') && (
                  <li>
                    <NavLink to="/dashboard" className={`${styleBaseLink} ${(pathname === "/" || pathname.includes("dashboard")) && styleSelectedLink}`} title="Dashboard">
                      <span className="material-symbols-outlined text-[22px]">dashboard</span>
                      <span className={textClass}>Dashboard</span>
                    </NavLink>
                  </li>
                )}

                {/* Clientes */}
                {canRead('customers') && (
                  <li>
                    <NavLink to="/customers" className={`${styleBaseLink} ${pathname.includes("customers") && styleSelectedLink}`} title="Clientes CRM">
                      <span className="material-symbols-outlined text-[22px]">group</span>
                      <span className={textClass}>Clientes</span>
                    </NavLink>
                  </li>
                )}

                {/* Chats */}
                {canRead('chats') && (
                  <li>
                    <NavLink to="/chats" className={`${styleBaseLink} ${pathname.includes("chats") && styleSelectedLink}`} title="WhatsApp">
                      <span className="material-symbols-outlined text-[22px]">chat</span>
                      <span className={textClass}>WhatsApp</span>
                    </NavLink>
                  </li>
                )}

                {/* Plantillas */}
                {canRead('chats') && (
                  <li>
                    <NavLink to="/templates" className={`${styleBaseLink} ${pathname.includes("templates") && styleSelectedLink}`} title="Plantillas de Mensajes">
                      <span className="material-symbols-outlined text-[22px]">content_copy</span>
                      <span className={textClass}>Plantillas</span>
                    </NavLink>
                  </li>
                )}

                {/* Tareas */}
                {canRead('tasks') && (
                  <li>
                    <NavLink to="/tasks" className={`${styleBaseLink} ${pathname.includes("tasks") && styleSelectedLink}`} title="Tareas">
                      <span className="material-symbols-outlined text-[22px]">task_alt</span>
                      <span className={textClass}>Tareas</span>
                    </NavLink>
                  </li>
                )}

                {/* Ventas & Reservas */}
                {showSalesGroup && (
                  <li>
                    <div
                      className={`${styleBaseLink} ${(pathname.includes("cotizations") || pathname.includes("cruises") || pathname.includes("reservations") || pathname.includes("flights") || pathname.includes("hotel-reservations") || pathname.includes("excursions") || pathname.includes("transfers") || pathname.includes("promotions")) && 'bg-[#3e56c4] font-bold text-white shadow-sm'}`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (isCollapsed) setIsCollapsed(false);
                        setSalesOpen(!salesOpen);
                      }}
                      title="Ventas"
                    >
                      <div className="flex items-center flex-1">
                        <span className="material-symbols-outlined text-[22px]">point_of_sale</span>
                        <span className={textClass}>Ventas</span>
                      </div>
                      {!isCollapsed && !salesOpen && activeReservations > 0 && (
                        <Badge count={activeReservations} />
                      )}
                      {!isCollapsed && (
                        <span className={`material-symbols-outlined text-[18px] ${salesOpen || activeReservations === 0 ? 'ml-auto' : 'ml-2'} transition-transform duration-300`}>
                          {salesOpen ? 'expand_less' : 'expand_more'}
                        </span>
                      )}
                    </div>

                    <div className={`overflow-hidden transition-all duration-300 ${salesOpen && !isCollapsed ? 'max-h-[420px] opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                      <ul className="flex flex-col gap-1.5 pl-6 pr-2 relative">
                        <div className="absolute left-[24px] top-0 bottom-2 w-px bg-white/10"></div>
                        {canRead('packages') && (
                          <li>
                            <NavLink to="/reservations" className={subLinkClass(pathname.includes("reservations") || pathname.includes("cotizations"))}>
                              <span className="material-symbols-outlined text-[16px]">map</span>
                              Paquetes
                            </NavLink>
                          </li>
                        )}
                        {canRead('cruises') && (
                          <li>
                            <NavLink to="/cruises" className={subLinkClass(pathname.includes("cruises"))}>
                              <span className="material-symbols-outlined text-[16px]">directions_boat</span>
                              Cruceros
                            </NavLink>
                          </li>
                        )}
                        {(canRead('flights') || canRead('packages')) && (
                          <li>
                            <NavLink to="/flights" className={subLinkClass(pathname.includes("flights"))}>
                              <span className="material-symbols-outlined text-[16px]">flight</span>
                              Vuelos
                            </NavLink>
                          </li>
                        )}
                        {canRead('hotel_reservations') && (
                          <li>
                            <NavLink to="/hotel-reservations" className={subLinkClass(pathname.includes("hotel-reservations"))}>
                              <span className="material-symbols-outlined text-[16px]">hotel</span>
                              Alojamiento
                            </NavLink>
                          </li>
                        )}
                        {canRead('excursions') && (
                          <li>
                            <NavLink to="/excursions" className={subLinkClass(pathname.includes("excursions") && !pathname.includes("tours"))}>
                              <span className="material-symbols-outlined text-[16px]">hiking</span>
                              Excursiones
                            </NavLink>
                          </li>
                        )}
                        {canRead('transfers') && (
                          <li>
                            <NavLink to="/transfers" className={subLinkClass(pathname.includes("transfers"))}>
                              <span className="material-symbols-outlined text-[16px]">local_taxi</span>
                              Traslados
                            </NavLink>
                          </li>
                        )}
                        {canRead('promotions') && (
                          <li>
                            <NavLink to="/promotions" className={subLinkClass(pathname.includes("promotions"))}>
                              <span className="material-symbols-outlined text-[16px]">local_offer</span>
                              Promociones
                            </NavLink>
                          </li>
                        )}

                      </ul>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* 1.5 SEGUIMIENTO */}
          {(canRead('checkin') || canRead('retornos')) && (
            <div className="mt-8 mb-6">
              <h3 className={`mb-3 text-[10px] font-bold tracking-widest text-[#64748B] uppercase duration-300 ${isCollapsed ? 'opacity-0 h-0 hidden' : 'opacity-100 px-4'}`}>
                Seguimiento
              </h3>
              <ul className="flex flex-col gap-2">
                {canRead('checkin') && (
                  <li>
                    <NavLink to="/seguimiento/checkin" className={`${styleBaseLink} ${pathname.includes('seguimiento/checkin') && styleSelectedLink}`} title="Check-in Próximo">
                      <span className="material-symbols-outlined text-[22px]">flight_takeoff</span>
                      <span className={textClass}>Check-in Próximo</span>
                      <Badge count={urgentCheckins} variant="danger" />
                    </NavLink>
                  </li>
                )}
                {canRead('retornos') && (
                  <li>
                    <NavLink to="/seguimiento/retornos" className={`${styleBaseLink} ${pathname.includes('seguimiento/retornos') && styleSelectedLink}`} title="Retornos Próximos">
                      <span className="material-symbols-outlined text-[22px]">flight_land</span>
                      <span className={textClass}>Retornos Próximos</span>
                    </NavLink>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* 3. CATALOGOS GLOBALES */}
          {showCatalogGroup && (
            <ul className="flex flex-col gap-2">
              <li>
                <div
                  className={`${styleBaseLink} ${(pathname.includes('destinations') || pathname.includes('aerolines') || pathname.includes('hotels') || pathname.includes('tours') || pathname.includes('operators') || pathname.includes('cruise-ships') || pathname.includes('cruise-cabins')) && 'bg-[#3e56c4] text-white font-bold shadow-sm'}`}
                  onClick={() => {
                    if (isCollapsed) setIsCollapsed(false);
                    setAdminOpen(!adminOpen)
                  }}
                  title="Catálogos del Sistema"
                >
                  <div className="flex items-center">
                    <span className="material-symbols-outlined text-[22px]">database</span>
                    <span className={textClass}>Catálogos</span>
                  </div>
                  {!isCollapsed && (
                    <span className="material-symbols-outlined text-[18px] ml-auto transition-transform duration-300">
                      {adminOpen ? 'expand_less' : 'expand_more'}
                    </span>
                  )}
                </div>

                <div className={`overflow-hidden transition-all duration-300 ${adminOpen && !isCollapsed ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                  <ul className="flex flex-col gap-1.5 pl-6 pr-2 relative">
                    <div className="absolute left-[24px] top-0 bottom-2 w-px bg-white/10"></div>
                    {canRead('destinations') && (
                      <li>
                        <NavLink to="/destinations" className={subLinkClass(pathname.includes('destinations'))}>
                          <span className="material-symbols-outlined text-[16px]">pin_drop</span> Destinos
                        </NavLink>
                      </li>
                    )}
                    {canRead('airlines') && (
                      <li>
                        <NavLink to="/aerolines" className={subLinkClass(pathname.includes('aerolines'))}>
                          <span className="material-symbols-outlined text-[16px]">airlines</span> Aerolíneas
                        </NavLink>
                      </li>
                    )}
                    {canRead('hotels') && (
                      <li>
                        <NavLink to="/hotels" className={subLinkClass(pathname.includes('hotels'))}>
                          <span className="material-symbols-outlined text-[16px]">apartment</span> Hoteles
                        </NavLink>
                      </li>
                    )}
                    {canRead('tours') && (
                      <li>
                        <NavLink to="/tours" className={subLinkClass(pathname.includes('tours'))}>
                          <span className="material-symbols-outlined text-[16px]">hiking</span> Excursiones
                        </NavLink>
                      </li>
                    )}
                    {canRead('operators') && (
                      <li>
                        <NavLink to="/operators" className={subLinkClass(pathname.includes('operators'))}>
                          <span className="material-symbols-outlined text-[16px]">real_estate_agent</span> Proveedores
                        </NavLink>
                      </li>
                    )}
                    {canRead('cruise_ships') && (
                      <li>
                        <NavLink to="/cruise-ships" className={subLinkClass(pathname.includes('cruise-ships'))}>
                          <span className="material-symbols-outlined text-[16px]">directions_boat</span> Barcos de Crucero
                        </NavLink>
                      </li>
                    )}
                    {canRead('cruise_cabins') && (
                      <li>
                        <NavLink to="/cruise-cabins" className={subLinkClass(pathname.includes('cruise-cabins'))}>
                          <span className="material-symbols-outlined text-[16px]">bed</span> Cabinas de Crucero
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </div>
              </li>
            </ul>
          )}

          {/* 2. FINANZAS Y CONTABILIDAD */}
          {showFinanceGroup && (
            <div className="mt-8 mb-6">
              <h3 className={`mb-3 text-[10px] font-bold tracking-widest text-[#64748B] uppercase duration-300 ${isCollapsed ? 'opacity-0 h-0 hidden' : 'opacity-100 px-4'}`}>
                Finanzas
              </h3>
              <ul className="flex flex-col gap-2">
                {canRead('treasury') && (
                  <li>
                    <NavLink to="/treasury" className={`${styleBaseLink} ${pathname.includes("treasury") && styleSelectedLink}`} title="Tesorería">
                      <span className="material-symbols-outlined text-[22px]">account_balance_wallet</span>
                      <span className={textClass}>Tesorería</span>
                    </NavLink>
                  </li>
                )}
                {canRead('accounting') && (
                  <li>
                    <NavLink to="/accounting" className={`${styleBaseLink} ${pathname.includes('accounting') && styleSelectedLink}`} title="Contabilidad">
                      <span className="material-symbols-outlined text-[22px]">receipt_long</span>
                      <span className={textClass}>Contabilidad</span>
                    </NavLink>
                  </li>
                )}
                {canRead('gastos_operativos') && (
                  <li>
                    <NavLink to="/gastos-operativos" className={`${styleBaseLink} ${pathname.includes('gastos-operativos') && styleSelectedLink}`} title="Gastos Operativos">
                      <span className="material-symbols-outlined text-[22px]">payments</span>
                      <span className={textClass}>Gastos Operativos</span>
                    </NavLink>
                  </li>
                )}
              </ul>
            </div>
          )}



          {/* 4. INTEGRACIONES B2B */}
          <div className="mt-8 mb-6">
            <h3 className={`mb-3 text-[10px] font-bold tracking-widest text-[#64748B] uppercase duration-300 ${isCollapsed ? 'opacity-0 h-0 hidden' : 'opacity-100 px-4'}`}>
              Conexiones
            </h3>
            <ul className="flex flex-col gap-1.5">
              <li>
                <NavLink to="/integrations/turbobeds" className={`${styleBaseLink} !py-2 opacity-70 hover:opacity-100`} title="TurboBeds">
                  <span className="material-symbols-outlined text-[20px]">hotel</span>
                  <span className={textClass}>TurboBeds</span>
                </NavLink>
              </li>
            </ul>
          </div>

          {/* 5. SEGURIDAD Y AJUSTES */}
          {(isAdmin || canRead('audit_log')) && (
            <div className="mt-8 mb-6">
              <h3 className={`mb-3 text-[10px] font-bold tracking-widest text-[#64748B] uppercase duration-300 ${isCollapsed ? 'opacity-0 h-0 hidden' : 'opacity-100 px-4'}`}>
                Sistema
              </h3>
              <ul className="flex flex-col gap-2">
                {isAdmin && (
                  <>
                    <li>
                      <NavLink to="/users" className={`${styleBaseLink} ${pathname.includes("users") && styleSelectedLink}`} title="Usuarios y Staff">
                        <span className="material-symbols-outlined text-[22px]">manage_accounts</span>
                        <span className={textClass}>Usuarios</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/roles" className={`${styleBaseLink} ${pathname.includes("roles") && styleSelectedLink}`} title="Gestión de Roles">
                        <span className="material-symbols-outlined text-[22px]">shield_person</span>
                        <span className={textClass}>Roles</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/settings" className={`${styleBaseLink} ${pathname.includes("settings") && styleSelectedLink}`} title="Ajustes de Sistema">
                        <span className="material-symbols-outlined text-[22px]">settings</span>
                        <span className={textClass}>Ajustes</span>
                      </NavLink>
                    </li>
                  </>
                )}
                {(isAdmin || canRead('audit_log')) && (
                  <li>
                    <NavLink to="/audit-log" className={`${styleBaseLink} ${pathname.includes("audit-log") && styleSelectedLink}`} title="Log de Auditoría">
                      <span className="material-symbols-outlined text-[22px]">history</span>
                      <span className={textClass}>Auditoría</span>
                    </NavLink>
                  </li>
                )}
              </ul>
            </div>
          )}

        </nav>
      </div>

      {/* <!-- Sidebar Footer / Collapse Button --> */}
      <div className="mt-auto pt-4 pb-6 px-4 w-full border-t border-white/5">
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-end'}`}>
          <button
            onClick={toggleCollapse}
            className={`hidden lg:flex w-10 h-10 items-center justify-center bg-white/5 hover:bg-white/10 rounded-xl text-slate-400 hover:text-white transition-colors ${isCollapsed ? 'mx-auto' : ''}`}
            title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            <span className="material-symbols-outlined text-[24px]">
              {isCollapsed ? 'keyboard_double_arrow_right' : 'keyboard_double_arrow_left'}
            </span>
          </button>
          <button
            onClick={() => setSidebarOpen(false)}
            className="flex lg:hidden w-10 h-10 items-center justify-center bg-white/5 hover:bg-white/10 rounded-xl text-slate-400 hover:text-white transition-colors"
          >
            <span className="material-symbols-outlined text-[24px]">close</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
